import React from "react";
// TRANSLATION
import { useTranslation } from "react-i18next";
// ANT DESIGN COMPONENTS
import { Button, InputNumber, Select, Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import OpcUaTextField from "./OpcUaTextField";
// INTERFACES
import type {
  OpcUaDataKind,
  OpcUaDataValueForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Option } = Select;

/** Human-readable labels for every Data-union kind. */
const DATA_KIND_OPTIONS: { value: OpcUaDataKind; label: string }[] = [
  { value: "bool", label: "Bool" },
  { value: "sbyte", label: "SByte" },
  { value: "byte", label: "Byte" },
  { value: "u16", label: "UInt16" },
  { value: "i16", label: "Int16" },
  { value: "u32", label: "UInt32" },
  { value: "i32", label: "Int32" },
  { value: "u64", label: "UInt64" },
  { value: "i64", label: "Int64" },
  { value: "float", label: "Float" },
  { value: "double", label: "Double" },
  { value: "string", label: "String" },
  { value: "datetime", label: "DateTime" },
  { value: "guid", label: "Guid" },
  { value: "bytestring", label: "ByteString" },
  { value: "statuscode", label: "StatusCode" },
  { value: "localizedtext", label: "LocalizedText (locale;text)" },
  { value: "qualifiedname", label: "QualifiedName (ns;name)" },
  { value: "nodeid", label: "NodeId" },
  { value: "expandednodeid", label: "ExpandedNodeId" },
  { value: "xmlelement", label: "XmlElement" },
  { value: "array", label: "Array" },
];

/** Placeholder hint text per kind — clarifies the compound-string encodings. */
const KIND_PLACEHOLDER: Partial<Record<OpcUaDataKind, string>> = {
  bool: "true / false",
  bytestring: "1,2,255",
  localizedtext: "en-US;Hello",
  qualifiedname: "2;MyName",
  u64: "0",
  i64: "0",
  datetime: "0",
};

/** A fresh value of `kind`, carrying the extra state that kind needs (a NodeId, an element list). */
function blankValueOfKind(kind: OpcUaDataKind): OpcUaDataValueForm {
  if (kind === "nodeid") return { kind, value: "", nodeId: { ...BLANK_NUMERIC_NODE_ID } };
  if (kind === "expandednodeid") {
    return {
      kind,
      value: "",
      nodeId: { ...BLANK_NUMERIC_NODE_ID },
      namespaceUri: "",
      serverIndex: 0,
    };
  }
  if (kind === "array") return { kind, value: "", elements: [] };
  return { kind, value: "" };
}

/**
 * Shared editor for one OpcUaDataValueForm (the 22-kind Data/Variant union
 * used by node values, method arguments, and literal filter operands).
 *
 * NodeId and ExpandedNodeId values are edited with the shared `NodeIdEditor`
 * (all five identifier kinds); an Array value is an ordered list of nested
 * values, each edited by this same component.
 *
 * Renders as a fragment — the caller keeps the grid-column wrapper — so it
 * can sit in the same row as sibling fields (e.g. Attribute in
 * MappingsEditor), matching every other OPC UA CDO editor's layout.
 */
export default function DataValueEditor({
  value,
  onChange,
  label = "Value",
  valueTooltip = undefined,
  disabled = false,
}: {
  value: OpcUaDataValueForm;
  onChange: (value: OpcUaDataValueForm) => void;
  label?: string;
  /** Overrides the generic value-format tooltip with one explaining what this value means (e.g. "trigger value" vs "value to write"). */
  valueTooltip?: React.ReactNode;
  disabled?: boolean;
}) {
  const [t] = useTranslation("global");

  const kindSelect = (
    <div className="col-12 col-md-4 mt-2">
      <OpcUaFieldLabel label={`${label} Type`} tooltip={t("general.opcUaDataValueTypeHelp")} />
      <Select
        className="generalStyles__width100"
        size="large"
        style={{ width: "100%", marginTop: 8 }}
        value={value.kind}
        disabled={disabled}
        onChange={(kind: OpcUaDataKind) => onChange(blankValueOfKind(kind))}
      >
        {DATA_KIND_OPTIONS.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
    </div>
  );

  if (value.kind === "array") {
    const elements = value.elements ?? [];
    const onChangeElements = (next: OpcUaDataValueForm[]) => onChange({ ...value, elements: next });
    return (
      <>
        {kindSelect}
        <div className="col-12 mt-2">
          <OpcUaFieldLabel label={`${label} Elements (${elements.length})`} />
          {elements.map((element, index) => (
            <div
              // Position-keyed: the element's own fields edit it in place.
              key={index}
              className="mt-2 generalStyles__flex"
              style={{ backgroundColor: "#161616", borderRadius: "5px", padding: "8px" }}
            >
              <div className="row" style={{ width: "100%" }}>
                <DataValueEditor
                  value={element}
                  onChange={(updated) => {
                    const next = [...elements];
                    next[index] = updated;
                    onChangeElements(next);
                  }}
                  label={`Element ${index}`}
                  disabled={disabled}
                />
              </div>
              <Tooltip title="Remove element">
                <Button
                  aria-label="Remove element"
                  onClick={() => onChangeElements(elements.filter((_, i) => i !== index))}
                  disabled={disabled}
                  className="buttonStyle__29"
                  shape="circle"
                  icon={<FontAwesomeIcon icon={faTrash} />}
                />
              </Tooltip>
            </div>
          ))}
          <Button
            onClick={() => onChangeElements([...elements, { kind: "string", value: "" }])}
            disabled={disabled}
            className="buttonStyle__28 mt-2"
          >
            <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
            Add Element
          </Button>
        </div>
      </>
    );
  }

  if (value.kind === "nodeid" || value.kind === "expandednodeid") {
    return (
      <>
        {kindSelect}
        <div className="col-12">
          <NodeIdEditor
            node={value.nodeId ?? BLANK_NUMERIC_NODE_ID}
            onChangeNode={(nodeId) => onChange({ ...value, nodeId })}
            label={label}
            disabled={disabled}
          />
          {value.kind === "expandednodeid" && (
            <div className="row">
              <div className="col-12 col-md-8 mt-2">
                <OpcUaTextField
                  label="Namespace URI"
                  value={value.namespaceUri ?? ""}
                  onChange={(namespaceUri) => onChange({ ...value, namespaceUri })}
                  disabled={disabled}
                />
              </div>
              <div className="col-12 col-md-4 mt-2">
                <OpcUaFieldLabel label="Server Index" />
                <InputNumber
                  className="generalStyles__width100"
                  size="large"
                  style={{ width: "100%", marginTop: 8 }}
                  min={0}
                  value={value.serverIndex ?? 0}
                  disabled={disabled}
                  onChange={(serverIndex) => onChange({ ...value, serverIndex: serverIndex ?? 0 })}
                />
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {kindSelect}
      <div className="col-12 col-md-4 mt-2">
        <OpcUaTextField
          label={
            KIND_PLACEHOLDER[value.kind] ? `${label} (${KIND_PLACEHOLDER[value.kind]})` : label
          }
          value={value.value}
          onChange={(next) => onChange({ ...value, value: next })}
          tooltip={valueTooltip ?? t("general.opcUaDataValueHelp")}
          disabled={disabled}
        />
      </div>
    </>
  );
}
